import { CheckCircle, Award, Calendar, Users, BarChart3, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CompleteStep = ({ navigate }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <div className="bg-gradient-to-br from-green-100 to-green-200 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
        <CheckCircle className="h-12 w-12 text-green-600" />
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-2">You're All Set! 🎉</h2>
      <p className="text-lg text-gray-600">
        Your clinic is now ready to manage memberships with MyMedMember
      </p>
    </div>

    {/* What's Next */}
    <Card className="border-2 border-teal-200 bg-teal-50">
      <CardContent className="pt-6">
        <h3 className="font-semibold text-teal-900 mb-4 text-lg">What's Next?</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-purple-100 p-2 rounded">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Membership Plans</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Create or review your membership offerings
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/admin/plans')}
              className="w-full"
            >
              Manage Plans
            </Button>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-blue-100 p-2 rounded">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Add Members</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Start adding your first patients
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/admin/members')}
              className="w-full"
            >
              Add Members
            </Button>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-green-100 p-2 rounded">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Appointments</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Schedule patient appointments
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/admin/appointments')}
              className="w-full"
            >
              Manage Appointments
            </Button>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-100 p-2 rounded">
                <BarChart3 className="h-5 w-5 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900">Analytics</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              View your clinic's performance
            </p>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/admin/ai-analytics')}
              className="w-full"
            >
              View Analytics
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Key Features */}
    <Card className="border-2">
      <CardContent className="pt-6">
        <h3 className="font-semibold text-gray-900 mb-4">Key Features at Your Fingertips</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>Membership Management</strong> - Create and manage plans effortlessly
            </span>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>Patient Portal</strong> - Members can self-service online
            </span>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>Appointment Scheduling</strong> - Easy booking and management
            </span>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>Payment Processing</strong> - Secure online payments
            </span>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>AI Analytics</strong> - Data-driven insights for growth
            </span>
          </div>
          
          <div className="flex items-start gap-2">
            <span className="text-teal-600 mt-0.5">✓</span>
            <span className="text-gray-700">
              <strong>Reports & Exports</strong> - Comprehensive business intelligence
            </span>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Resources */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <HelpCircle className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-semibold text-blue-900 mb-2">Need Help Getting Started?</h4>
          <p className="text-sm text-blue-800 mb-3">
            Access our comprehensive resources to make the most of MyMedMember
          </p>
          <div className="flex flex-wrap gap-2">
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-300 hover:bg-blue-100"
              onClick={() => navigate('/help')}
            >
              Help Center
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-300 hover:bg-blue-100"
            >
              Video Tutorials
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="border-blue-300 hover:bg-blue-100"
            >
              Download Guide
            </Button>
          </div>
        </div>
      </div>
    </div>

    {/* Final CTA */}
    <div className="text-center pt-4">
      <p className="text-gray-600 mb-4">
        Ready to transform your practice with membership medicine?
      </p>
      <Button 
        onClick={() => navigate('/admin')}
        className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-lg px-8 py-6"
      >
        Go to Dashboard →
      </Button>
    </div>
  </div>
);

export default CompleteStep;
